import {some} from '@danwang/option';
import {
  useGenerateBenefitCreditsMutation,
  FeePeriodsByAccountDocument,
} from '@transactions/graphql';
import {today} from 'common/date';
import * as React from 'react';
import {Button} from 'src/Button';
import {formatCents, formatPeriodLabel} from 'src/CardBenefitsView/benefitUtils';
import CategoryPicker from 'src/CategoryPicker';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {Option} from '@danwang/option';
import type {FullCardBenefitFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type GenerateBenefitCreditsModalUserProps = {
  benefit: FullCardBenefitFragment;
  /** Fee period payee, the default when the benefit has none of its own. */
  feePeriodPayee?: string | null;
  onSuccess: () => void;
};

export const GenerateBenefitCreditsModal = ({
  onRequestClose,
  benefit,
  feePeriodPayee,
  onSuccess,
}: GenerateBenefitCreditsModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [payee, setPayee] = React.useState(
    feePeriodPayee ?? benefit.name,
  );
  const [category, setCategory] = React.useState<Option<string>>(
    some(benefit.category.name),
  );
  const [description, setDescription] = React.useState(benefit.name);
  const [error, setError] = React.useState<string | null>(null);

  // Only periods that still have room can be filled; sorted so the checkbox
  // list reads chronologically regardless of stored order.
  const fillable = React.useMemo(
    () =>
      benefit.periods
        .filter((p) => p.remainingAmount > 0)
        .slice()
        .sort((a, b) => a.startDate - b.startDate),
    [benefit.periods],
  );

  // Default to periods that have already started: precreating a credit dated
  // in the future is the surprising case, so it's opt-in per period.
  const nowSeconds = today().toSeconds();
  const [selected, setSelected] = React.useState<ReadonlySet<number>>(
    () =>
      new Set(
        fillable
          .filter((p) => p.startDate <= nowSeconds)
          .map((p) => p.startDate),
      ),
  );

  const toggle = (startDate: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(startDate)) {
        next.delete(startDate);
      } else {
        next.add(startDate);
      }
      return next;
    });
  };

  const [generateCredits, {loading}] = useGenerateBenefitCreditsMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await generateCredits({
        variables: {
          benefitId: benefit._id,
          periodStartDates: fillable
            .filter((p) => selected.has(p.startDate))
            .map((p) => p.startDate),
          payee: payee.trim() || undefined,
          categoryName: category.getOrElse('') || undefined,
          description: description.trim() || undefined,
        },
        refetchQueries: [
          {
            query: FeePeriodsByAccountDocument,
            variables: {accountId: benefit.accountId},
          },
        ],
      });
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      setError((e as Error).message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={loading || category.isEmpty || selected.size === 0}
      >
        {loading
          ? 'Creating...'
          : fillable.length === 0
            ? 'Nothing to fill'
            : selected.size === 0
              ? 'Select periods to fill'
              : `Create ${selected.size} credit${selected.size === 1 ? '' : 's'}`}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Create benefit credits"
      footer={footer}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <p className="text-muted-foreground text-sm">
          Fills each selected period to its remaining amount on{' '}
          {benefit.creditAccount?.name ?? 'the credit account'}, linked to this
          benefit. Already-covered periods are skipped; periods that
          haven&rsquo;t started yet are unchecked by default.
        </p>

        {fillable.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Every period is already fully covered.
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between">
              <label className="text-sm font-medium">Periods to fill</label>
              <span className="flex gap-2 text-xs">
                <button
                  type="button"
                  className="text-muted-foreground underline"
                  onClick={() =>
                    setSelected(new Set(fillable.map((p) => p.startDate)))
                  }
                >
                  All
                </button>
                <button
                  type="button"
                  className="text-muted-foreground underline"
                  onClick={() => setSelected(new Set())}
                >
                  None
                </button>
              </span>
            </div>
            <div className="border-border flex flex-col divide-y rounded-md border">
              {fillable.map((p) => (
                <label
                  key={p.startDate}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    className="shrink-0"
                    checked={selected.has(p.startDate)}
                    onChange={() => toggle(p.startDate)}
                  />
                  <span className="flex-1 truncate">
                    {formatPeriodLabel(p.startDate, p.endDate)}
                    {p.startDate > nowSeconds && (
                      <span className="text-muted-foreground ml-1.5 text-xs">
                        upcoming
                      </span>
                    )}
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {formatCents(p.remainingAmount)}
                    {p.usedAmount > 0 && ` of ${formatCents(p.amount)}`}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Payee</label>
          <TextInput value={payee} onChange={setPayee} autoFocus />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Category</label>
          <CategoryPicker
            selectedCategory={category}
            onSelectCategory={setCategory}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Description</label>
          <TextInput value={description} onChange={setDescription} />
        </div>
      </form>
    </Modal>
  );
};
